import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TerminalIcon from '@mui/icons-material/Terminal';
import BugReportIcon from '@mui/icons-material/BugReport';
import api from '../../services/api';
import { Scenario, ScenarioDebugState } from '../../types/attacks';
import SshTerminalView, { SshConnState } from '../regions/SshTerminalView';

interface ScenarioDebugDialogProps {
  open: boolean;
  // Only id + name are used, so callers that only have those (e.g. the runbook
  // scenario menu) can open the dialog without a full Scenario object.
  scenario: Pick<Scenario, 'id' | 'name'> | null;
  onClose: () => void;
}

type Phase = 'starting' | 'provisioning' | 'ready' | 'error';

const POLL_INTERVAL_MS = 3000;

/**
 * Orchestrates a scenario "Run Debug" run: start (create one bot) → poll until the
 * bot is running with a public IP → embed the SSH terminal. Terminates the debug
 * bot when the dialog is closed.
 */
const ScenarioDebugDialog: React.FC<ScenarioDebugDialogProps> = ({ open, scenario, onClose }) => {
  const [phase, setPhase] = useState<Phase>('starting');
  const [debugState, setDebugState] = useState<ScenarioDebugState | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [termState, setTermState] = useState<SshConnState>('connecting');
  const [stopping, setStopping] = useState(false);

  const pollTimer = useRef<number | null>(null);
  const startedRef = useRef(false);
  const scenarioId = scenario?.id ?? null;

  const clearPoll = useCallback(() => {
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const applyState = useCallback((state: ScenarioDebugState) => {
    setDebugState(state);
    if (state.status === 'error') {
      setPhase('error');
      setErrorMsg(state.error || 'The debug bot entered an error state.');
    } else if (state.ready && state.public_ip) {
      setPhase('ready');
    } else {
      setPhase('provisioning');
    }
  }, []);

  // Start the debug run once when the dialog opens.
  useEffect(() => {
    if (!open || !scenarioId || startedRef.current) return;
    startedRef.current = true;
    setPhase('starting');
    setErrorMsg(null);
    setDebugState(null);
    (async () => {
      try {
        const state = await api.startScenarioDebug(scenarioId);
        applyState(state);
      } catch (err: any) {
        setPhase('error');
        setErrorMsg(err?.response?.data?.detail || err?.message || 'Failed to start debug run.');
      }
    })();
  }, [open, scenarioId, applyState]);

  // Poll for readiness while provisioning.
  useEffect(() => {
    if (!open || !scenarioId) return undefined;
    if (phase !== 'provisioning' && phase !== 'starting') return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const state = await api.getScenarioDebug(scenarioId);
        if (cancelled) return;
        if (state.active === false) {
          // Debug bot disappeared (terminated/error-cleaned) — surface it.
          setPhase('error');
          setErrorMsg('The debug bot is no longer active.');
          return;
        }
        applyState(state);
        if (!cancelled && !state.ready && state.status !== 'error') {
          pollTimer.current = window.setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch {
        if (!cancelled) pollTimer.current = window.setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    pollTimer.current = window.setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [open, scenarioId, phase, applyState, clearPoll]);

  // Terminate the debug bot and reset when the dialog closes.
  const handleClose = useCallback(async () => {
    clearPoll();
    const id = scenarioId;
    // Reset local state immediately so a reopen starts clean.
    startedRef.current = false;
    setPhase('starting');
    setDebugState(null);
    setErrorMsg(null);
    setTermState('connecting');
    setStopping(false);
    onClose();
    if (id) {
      try {
        await api.stopScenarioDebug(id);
      } catch {
        /* best-effort teardown; supervisor safety-net will reap orphans */
      }
    }
  }, [scenarioId, onClose, clearPoll]);

  const handleStopClick = useCallback(async () => {
    setStopping(true);
    await handleClose();
  }, [handleClose]);

  const statusLine = (() => {
    if (phase === 'ready') {
      return termState === 'open' ? 'connected' : termState === 'error' ? 'terminal error' : 'connecting…';
    }
    if (phase === 'error') return 'error';
    const status = debugState?.status;
    return status ? `provisioning · ${status}` : 'provisioning…';
  })();
  const statusColor =
    phase === 'error' || termState === 'error'
      ? 'error.main'
      : phase === 'ready' && termState === 'open'
      ? 'success.main'
      : 'text.secondary';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <BugReportIcon fontSize="small" />
        <Typography component="span" sx={{ fontWeight: 600 }}>
          Run Debug · {scenario?.name || 'scenario'}
        </Typography>
        <Typography component="span" variant="caption" sx={{ color: statusColor, ml: 1 }}>
          {statusLine}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Stop debug run">
          <IconButton
            aria-label="stop debug run"
            onClick={handleStopClick}
            disabled={stopping}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={{ p: 1 }}>
        {phase === 'error' ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg || 'Debug run failed.'}
            </Alert>
            <Button variant="outlined" onClick={handleStopClick} startIcon={<CloseIcon />}>
              Close
            </Button>
          </Box>
        ) : phase === 'ready' && debugState?.bot_id ? (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                pb: 1,
                color: 'text.secondary',
              }}
            >
              <TerminalIcon fontSize="small" />
              <Typography variant="caption" sx={{ fontFamily: '"Space Mono", monospace' }}>
                {debugState.public_ip} · bot {debugState.bot_id.slice(0, 8)}
              </Typography>
            </Box>
            <SshTerminalView
              active={open}
              botId={debugState.bot_id}
              onStateChange={setTermState}
              height="60vh"
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              minHeight: '40vh',
            }}
          >
            <CircularProgress size={56} />
            <Typography
              variant="body1"
              sx={{ fontFamily: '"Space Mono", monospace', color: 'text.secondary', textAlign: 'center' }}
            >
              {phase === 'starting'
                ? 'Starting debug run — creating one bot…'
                : 'Provisioning the debug bot. The terminal opens automatically once it is ready.'}
            </Typography>
            {debugState?.status && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                status: {debugState.status}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScenarioDebugDialog;
